import React from 'react';
import { User, Post, Group } from '../types';

interface CommunitySidebarProps {
    user: User;
    posts: Post[];
    groups: Group[];
    activeFilter: 'All' | 'Update' | 'Question' | 'Insight' | 'Media';
    selectedGroupId: string | null;
    onFilterChange: (filter: 'All' | 'Update' | 'Question' | 'Insight' | 'Media') => void;
    onGroupSelect: (groupId: string | null) => void;
    onCreateGroup: () => void;
    onDiscoverGroups: () => void;
    onEditAvatar?: () => void;
}

const CommunitySidebar: React.FC<CommunitySidebarProps> = ({
    user,
    posts,
    groups,
    activeFilter,
    selectedGroupId,
    onFilterChange,
    onGroupSelect,
    onCreateGroup,
    onDiscoverGroups,
    onEditAvatar
}) => {
    return (
        <div className="w-full h-full flex flex-col bg-[#f2efe4] dark:bg-[#0f1521] border-r border-stone-200 dark:border-[#1e293b] shrink-0 p-4 space-y-4 overflow-y-auto custom-scrollbar">
             {/* Profile Card */}
             <div className="bg-white dark:bg-stone-800/50 p-6 rounded-xl shadow-sm border border-stone-200 dark:border-stone-700">
                <div className="flex flex-col items-center text-center mb-6">
                    <div className="relative group cursor-pointer mb-3">
                        <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full border-4 border-amber-100 dark:border-amber-900/50 object-cover" />
                        {onEditAvatar && (
                            <div 
                                onClick={onEditAvatar}
                                className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Change Avatar"
                            >
                                <i className="fa-solid fa-camera text-white"></i>
                            </div>
                        )}
                    </div>
                    <h3 className="font-bold text-stone-800 dark:text-white text-lg">{user.name}</h3>
                    <p className="text-xs text-stone-500 dark:text-stone-400 font-serif italic">Seeker of Truth</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center text-sm border-t border-stone-100 dark:border-stone-700 pt-4">
                    <div>
                    <span className="block font-bold text-lg text-amber-600">{posts.filter(p => p.author.name === user.name).length}</span>
                    <span className="text-stone-400 text-xs">Posts</span>
                    </div>
                    <div>
                    <span className="block font-bold text-lg text-amber-600">{posts.reduce((acc, p) => acc + (p.likedByMe ? 1 : 0), 0)}</span>
                    <span className="text-stone-400 text-xs">Likes Given</span>
                    </div>
                </div>
            </div>

            {/* Feeds/Categories */}
            <div className="bg-white dark:bg-stone-800/50 rounded-xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden">
                <div className="p-4 border-b border-stone-100 dark:border-stone-700">
                    <h4 className="font-bold text-stone-700 dark:text-stone-300 text-xs uppercase tracking-wider">Topics</h4>
                </div>
                <div className="flex flex-col">
                    {(['All', 'Update', 'Question', 'Insight', 'Media'] as const).map(filter => (
                    <button 
                        key={filter}
                        onClick={() => onFilterChange(filter)}
                        className={`px-4 py-2.5 text-left text-sm flex items-center justify-between hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors ${activeFilter === filter ? 'text-amber-700 dark:text-amber-400 font-bold bg-amber-50 dark:bg-amber-900/20' : 'text-stone-600 dark:text-stone-300'}`}
                    >
                        <span>{filter}s</span>
                    </button>
                    ))}
                </div>
            </div>

            {/* Groups List */}
            <div className="bg-white dark:bg-stone-800/50 rounded-xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden">
                <div className="p-4 border-b border-stone-100 dark:border-stone-700 flex justify-between items-center">
                    <h4 className="font-bold text-stone-700 dark:text-stone-300 text-xs uppercase tracking-wider">My Groups</h4>
                    <button onClick={onCreateGroup} className="text-stone-400 hover:text-amber-600 transition-colors" title="Create Group">
                        <i className="fa-solid fa-plus"></i>
                    </button>
                </div>
                <div className="flex flex-col max-h-60 overflow-y-auto custom-scrollbar">
                    <button 
                    onClick={() => onGroupSelect(null)}
                    className={`px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors border-l-4 ${!selectedGroupId ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-transparent'}`}
                    >
                    <div className="w-8 h-8 rounded-full bg-stone-200 dark:bg-stone-700 flex items-center justify-center text-stone-500 dark:text-stone-300">
                        <i className="fa-solid fa-globe"></i>
                    </div>
                    <span className={`font-medium ${!selectedGroupId ? 'text-amber-900 dark:text-amber-200' : 'text-stone-600 dark:text-stone-300'}`}>Global Feed</span>
                    </button>

                    {groups.filter(g => g.isMember).map(group => (
                    <button 
                        key={group.id}
                        onClick={() => onGroupSelect(group.id)}
                        className={`px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors border-l-4 ${selectedGroupId === group.id ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-transparent'}`}
                    >
                        <img src={group.image} alt={group.name} className="w-8 h-8 rounded-full object-cover" />
                        <div className="flex-1 min-w-0">
                            <span className={`block truncate font-medium ${selectedGroupId === group.id ? 'text-amber-900 dark:text-amber-200' : 'text-stone-600 dark:text-stone-300'}`}>{group.name}</span>
                        </div>
                    </button>
                    ))}
                </div>
                <div className="p-2 border-t border-stone-100 dark:border-stone-700">
                    <button 
                    onClick={onDiscoverGroups}
                    className="w-full py-2 text-xs text-amber-600 font-bold hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors flex items-center justify-center gap-2"
                    >
                        <i className="fa-regular fa-compass"></i>
                        Discover Groups
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CommunitySidebar;